/**
 * Ashir motion layer: scroll reveals and subtle parallax.
 * Content is visible by default; hidden states only apply once `html.ashir-js` is set,
 * and everything is skipped when the visitor prefers reduced motion.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initReveal(root = document) {
  const targets = root.querySelectorAll('[data-ashir-reveal]:not(.is-inview)');
  if (!targets.length) return;

  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-inview'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        if (el.hasAttribute('data-ashir-stagger')) {
          [...el.children].forEach((child, i) => child.style.setProperty('--ashir-i', i));
        }
        el.classList.add('is-inview');
        observer.unobserve(el);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
  );

  targets.forEach((el) => observer.observe(el));
}

const parallaxItems = new Set();
let ticking = false;

function updateParallax() {
  ticking = false;
  const vh = window.innerHeight;
  for (const el of parallaxItems) {
    const rect = el.parentElement.getBoundingClientRect();
    const speed = parseFloat(el.dataset.ashirParallax) || 0.12;
    // Offset relative to the viewport centre: 0 when the block is centred.
    const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
    el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
  }
}

function requestParallax() {
  if (!ticking && parallaxItems.size) {
    ticking = true;
    requestAnimationFrame(updateParallax);
  }
}

function initParallax(root = document) {
  if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

  const visibility = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) parallaxItems.add(entry.target);
      else parallaxItems.delete(entry.target);
    }
    requestParallax();
  });

  root.querySelectorAll('[data-ashir-parallax]').forEach((el) => visibility.observe(el));
}

function init(root) {
  initReveal(root);
  initParallax(root);
}

window.ashirMotionReady = true;
init();

window.addEventListener('scroll', requestParallax, { passive: true });
window.addEventListener('resize', requestParallax, { passive: true });

// Theme editor: re-run when sections are added or re-rendered.
document.addEventListener('shopify:section:load', (event) => init(event.target));
